"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { usePathname } from "next/navigation"
import { Clock, LogIn } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { logger } from "@/lib/logger"

const log = logger.scope("SessionTimeout")
// Note: This app uses PIN-based auth with server-side JWT cookies (not Appwrite browser sessions).
// Session refresh is handled entirely via /api/auth/refresh endpoint.

// Staff work 4+ hour dinner shifts - inactivity timeout set to 4 hours
const SESSION_TIMEOUT = 4 * 60 * 60 * 1000 // 4 hours of inactivity before expiry
const WARNING_BEFORE = 10 * 60 * 1000 // Show warning 10 minutes before
const CHECK_INTERVAL = 60 * 1000 // Check every minute
// Refresh server-side JWT every 30 minutes to keep it alive through a 12-hour shift
const SERVER_REFRESH_INTERVAL = 30 * 60 * 1000 // 30 minutes

// Pages where session management should not run
const AUTH_PAGES = ["/login", "/offline"]

export function SessionTimeout() {
  const pathname = usePathname()
  const [showWarning, setShowWarning] = useState(false)
  const [showExpired, setShowExpired] = useState(false)
  const [lastActivity, setLastActivity] = useState(Date.now())
  const [timeRemaining, setTimeRemaining] = useState(SESSION_TIMEOUT)
  const refreshInFlightRef = useRef(false)
  const hasRefreshedRef = useRef(false) // Track if we've ever refreshed successfully

  // Skip session management on auth/public pages
  const isAuthPage = AUTH_PAGES.includes(pathname)

  // Reset timer on activity
  const resetTimer = useCallback(() => {
    setLastActivity(Date.now())
    setShowWarning(false)
  }, [])

  // Handle user activity
  useEffect(() => {
    if (isAuthPage) return

    const events = ["mousedown", "keydown", "touchstart", "scroll"]

    const handleActivity = () => {
      if (!showExpired) {
        resetTimer()
      }
    }

    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true })
    })

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity)
      })
    }
  }, [resetTimer, showExpired, isAuthPage])

  // Check session status periodically
  useEffect(() => {
    if (isAuthPage) return

    const checkSession = async () => {
      const elapsed = Date.now() - lastActivity
      const remaining = SESSION_TIMEOUT - elapsed

      setTimeRemaining(remaining)

      if (remaining <= 0) {
        // Session expired due to inactivity
        setShowExpired(true)
        setShowWarning(false)
        // Server-side cookie will be cleared on next /api/auth/ call or on login
      } else if (remaining <= WARNING_BEFORE) {
        setShowWarning(true)
      } else {
        setShowWarning(false)
      }
    }

    const interval = setInterval(checkSession, CHECK_INTERVAL)
    checkSession()

    return () => clearInterval(interval)
  }, [lastActivity, isAuthPage])

  // Refresh JWT immediately when tablet wakes from sleep
  // Android suspends timers during screen-off, so setInterval may have drifted.
  // On wake, refresh JWT + reset inactivity timer so staff aren't kicked out.
  useEffect(() => {
    if (isAuthPage) return

    const handleVisibilityChange = async () => {
      if (document.hidden) return // Only care about becoming visible

      resetTimer()
      if (refreshInFlightRef.current) return
      refreshInFlightRef.current = true
      try {
        const res = await fetch("/api/auth/refresh", {
          method: "POST",
          credentials: "include",
        })
        if (!res.ok && res.status === 401 && hasRefreshedRef.current) {
          setShowExpired(true)
        } else if (res.ok) {
          hasRefreshedRef.current = true
        }
      } catch {
        // Network might still be reconnecting after sleep — will retry on next interval
      } finally {
        refreshInFlightRef.current = false
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange)
  }, [isAuthPage, resetTimer])

  // Proactive session refresh - refreshes the server-side JWT cookie
  // to prevent silent expiry during a shift.
  // Note: This app uses PIN-based auth with server-side JWT cookies,
  // NOT Appwrite browser sessions, so we only refresh via /api/auth/refresh.
  useEffect(() => {
    if (isAuthPage) return

    const refreshSession = async () => {
      if (showExpired || refreshInFlightRef.current) return
      refreshInFlightRef.current = true
      try {
        const res = await fetch("/api/auth/refresh", {
          method: "POST",
          credentials: "include",
        })
        if (!res.ok) {
          log.warn("Server refresh failed", { status: res.status })
          // Only trigger session expired if we've previously had a successful refresh.
          // This prevents false positives on initial page load after login redirect
          // when the cookie may not be fully established yet.
          if (res.status === 401 && hasRefreshedRef.current) {
            setShowExpired(true)
          }
        } else {
          hasRefreshedRef.current = true
        }
      } catch (e) {
        // Network error - will retry on next interval
        log.warn("Refresh error", { error: String(e) })
      } finally {
        refreshInFlightRef.current = false
      }
    }

    // Delay initial refresh by 3 seconds to let login redirect fully complete,
    // then refresh every 30 minutes
    const initialTimeout = setTimeout(refreshSession, 3000)
    const interval = setInterval(refreshSession, SERVER_REFRESH_INTERVAL)
    return () => {
      clearTimeout(initialTimeout)
      clearInterval(interval)
    }
  }, [showExpired, isAuthPage])

  const handleStayLoggedIn = async () => {
    try {
      // Ping server to keep session alive
      const res = await fetch("/api/auth/refresh", {
        method: "POST",
        credentials: "include",
      })
      if (res.ok) {
        hasRefreshedRef.current = true
        resetTimer()
      } else {
        setShowExpired(true)
      }
    } catch (_e) {
      setShowExpired(true)
    }
  }

  const handleLogin = () => {
    window.location.href = "/login"
  }

  const formatTime = (ms: number) => {
    const minutes = Math.floor(ms / 60000)
    const seconds = Math.floor((ms % 60000) / 1000)
    return `${minutes}:${seconds.toString().padStart(2, "0")}`
  }

  // Never show session UI on auth pages
  if (isAuthPage) return null

  if (showExpired) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200] p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-full">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <CardTitle>Session Expired</CardTitle>
                <CardDescription>
                  Your session has timed out due to inactivity
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              For security, you have been logged out. Please log in again to continue.
            </p>
            <Button onClick={handleLogin} className="w-full">
              <LogIn className="h-4 w-4 mr-2" />
              Log In Again
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (showWarning) {
    return (
      <div className="fixed bottom-4 right-4 z-[150] animate-in slide-in-from-bottom duration-300">
        <Card className="max-w-sm border-amber-200 bg-amber-50/95 shadow-lg">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-600" />
              <CardTitle className="text-base text-amber-900">Session Expiring Soon</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-amber-800">
              Your session will expire in{" "}
              <span className="font-bold">{formatTime(timeRemaining)}</span>
            </p>
            <Button onClick={handleStayLoggedIn} size="sm" className="w-full">
              Stay Logged In
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return null
}
